import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ImageUpload from '../components/ImageUpload';
import { Label } from '../components/ui/label';
import { RichTextEditor } from '../components/RichTextEditor';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Combobox, MultiCombobox } from '../components/Combobox';
import { ArrowLeft, Save, Plus, Trash2, X, Barcode, Info } from 'lucide-react';
import { toast } from 'sonner';
import { errMsg } from '../lib/errors';

const emptyForm = {
    sku: '', name: '', short_description: '', description: '',
    category_id: '', brand_id: '',
    hsn_code: '',
    barcodes: [],
    supplier_ids: [],
    retail_unit_code: 'pcs',
    wholesale_unit_code: 'pcs',
    stock_unit_code: '',
    unit_prices: [],  // new matrix
    images: [''], videos: [], specs: {},
    pricing: {  // legacy fallback
        base_price: 0, retail_mrp: 0, retail_selling_price: 0,
        wholesale_mrp: null, wholesale_selling_price: null,
        level_1_price: null, level_2_price: null, level_3_price: null,
        level_4_price: null, level_5_price: null,
    },
    logistics: { weight_kg: 0.5, volumetric_l_cm: 20, volumetric_w_cm: 20, volumetric_h_cm: 25, volumetric_divisor: 5000 },
    initial_inventory: 0, low_stock_threshold: 5, active: true, featured: false, tags: [],
    tax_group_id: '',
};

// ---------- Barcode chip input ----------
function BarcodeInput({ value, onChange }) {
    const [txt, setTxt] = useState('');
    const commit = () => {
        const v = txt.trim();
        if (!v) return;
        if (value.includes(v)) { setTxt(''); return; }
        onChange([...value, v]);
        setTxt('');
    };
    return (
        <div>
            <div className="flex flex-wrap gap-1.5 mb-2">
                {value.map((b, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-neutral-100 border rounded-md px-2 py-1 text-xs font-mono" data-testid={`barcode-chip-${i}`}>
                        <Barcode className="h-3 w-3" />{b}
                        <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`barcode-remove-${i}`}>
                            <X className="h-3 w-3 text-red-600" />
                        </button>
                    </span>
                ))}
                {value.length === 0 && <span className="text-xs text-neutral-400">No barcodes added yet</span>}
            </div>
            <div className="flex gap-2">
                <Input value={txt}
                    onChange={e => setTxt(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); } }}
                    placeholder="Scan or type barcode… (Enter to add)" data-testid="barcode-input" />
                <Button type="button" variant="outline" size="sm" onClick={commit} data-testid="barcode-add-button">Add</Button>
            </div>
            <div className="text-xs text-neutral-500 mt-1">Supports EAN-13, UPC, Code-128, custom internal codes — any format.</div>
        </div>
    );
}

// (SupplierPicker replaced by <MultiCombobox> below \u2014 kept import site intact.)


// ---------- Unit Price Matrix ----------
const WHOLESALE_LEVELS = [0, 1, 2, 3, 4, 5];  // 0 = default wholesale, 1..5 tiered

function findRow(matrix, unit_code, customer_type, level) {
    return matrix.find(r => r.unit_code === unit_code && r.customer_type === customer_type && Number(r.level || 0) === level);
}

function UnitPriceMatrix({ value, onChange, retailUnit, wholesaleUnit }) {
    const rows = value || [];
    const setCell = (unit_code, customer_type, level, field, val) => {
        const numeric = val === '' ? null : parseFloat(val);
        const idx = rows.findIndex(r => r.unit_code === unit_code && r.customer_type === customer_type && Number(r.level || 0) === level);
        const next = [...rows];
        if (idx === -1) {
            next.push({ unit_code, customer_type, level, mrp: 0, base_price: null, selling_price: 0, [field]: numeric });
        } else {
            next[idx] = { ...next[idx], [field]: numeric };
        }
        onChange(next);
    };
    const cellVal = (unit_code, customer_type, level, field) => {
        const r = findRow(rows, unit_code, customer_type, level);
        return r == null || r[field] == null ? '' : r[field];
    };
    const sameUnit = retailUnit && wholesaleUnit && retailUnit === wholesaleUnit;
    return (
        <div className="space-y-4">
            {/* Retail block */}
            <div className="rounded-lg border p-3">
                <div className="text-xs font-semibold text-neutral-700 mb-2">
                    RETAIL — unit: <span className="font-mono">{retailUnit || '?'}</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">Base Cost</Label>
                        <Input type="number" step="0.01" value={cellVal(retailUnit, 'retail', 0, 'base_price')}
                            onChange={e => setCell(retailUnit, 'retail', 0, 'base_price', e.target.value)}
                            data-testid="upm-retail-base" /></div>
                    <div><Label className="text-xs">MRP</Label>
                        <Input type="number" step="0.01" value={cellVal(retailUnit, 'retail', 0, 'mrp')}
                            onChange={e => setCell(retailUnit, 'retail', 0, 'mrp', e.target.value)}
                            data-testid="upm-retail-mrp" /></div>
                    <div><Label className="text-xs">Selling</Label>
                        <Input type="number" step="0.01" value={cellVal(retailUnit, 'retail', 0, 'selling_price')}
                            onChange={e => setCell(retailUnit, 'retail', 0, 'selling_price', e.target.value)}
                            data-testid="upm-retail-selling" /></div>
                </div>
            </div>

            {/* Wholesale block */}
            <div className="rounded-lg border p-3">
                <div className="text-xs font-semibold text-neutral-700 mb-2">
                    WHOLESALE — unit: <span className="font-mono">{wholesaleUnit || '?'}</span>
                    {sameUnit && <span className="ml-2 text-neutral-500 font-normal">(same as retail unit)</span>}
                </div>
                <div className="grid grid-cols-12 gap-2 items-end text-xs">
                    <div className="col-span-2 font-medium text-neutral-500">Tier</div>
                    <div className="col-span-3"><Label className="text-xs">Base Cost</Label></div>
                    <div className="col-span-3"><Label className="text-xs">MRP</Label></div>
                    <div className="col-span-4"><Label className="text-xs">Selling</Label></div>
                    {WHOLESALE_LEVELS.map(lv => (
                        <React.Fragment key={lv}>
                            <div className="col-span-2 font-medium">{lv === 0 ? 'Default' : `L${lv}`}</div>
                            <div className="col-span-3">
                                <Input type="number" step="0.01" value={cellVal(wholesaleUnit, 'wholesale', lv, 'base_price')}
                                    onChange={e => setCell(wholesaleUnit, 'wholesale', lv, 'base_price', e.target.value)}
                                    data-testid={`upm-ws-${lv}-base`} />
                            </div>
                            <div className="col-span-3">
                                <Input type="number" step="0.01" value={cellVal(wholesaleUnit, 'wholesale', lv, 'mrp')}
                                    onChange={e => setCell(wholesaleUnit, 'wholesale', lv, 'mrp', e.target.value)}
                                    data-testid={`upm-ws-${lv}-mrp`} />
                            </div>
                            <div className="col-span-4">
                                <Input type="number" step="0.01" value={cellVal(wholesaleUnit, 'wholesale', lv, 'selling_price')}
                                    onChange={e => setCell(wholesaleUnit, 'wholesale', lv, 'selling_price', e.target.value)}
                                    data-testid={`upm-ws-${lv}-selling`} />
                            </div>
                        </React.Fragment>
                    ))}
                </div>
                <div className="text-xs text-neutral-500 mt-2">Default row applies to all wholesale customers with no assigned level. L1–L5 override for tier customers.</div>
            </div>
        </div>
    );
}

function StockAdjuster({ productId, unit, currentQty, onAdjusted }) {
    const [mode, setMode] = useState('set');   // 'set' | 'delta'
    const [value, setValue] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const doSave = async () => {
        const v = parseFloat(value);
        if (isNaN(v)) { toast.error('Enter a number'); return; }
        if (!reason.trim()) { toast.error('Reason required'); return; }
        const change = mode === 'set' ? (v - currentQty) : v;
        if (change === 0) { toast.info('Nothing to change'); return; }
        setBusy(true);
        try {
            const r = await api.post('/inventory/adjust', { product_id: productId, change, reason: reason.trim() });
            toast.success(`Stock updated \u2192 ${r.data.new_qty} ${unit}`);
            onAdjusted && onAdjusted(r.data.new_qty);
            setValue(''); setReason('');
        } catch (e) { toast.error(errMsg(e, 'Adjust failed')); }
        finally { setBusy(false); }
    };
    return (
        <div className="rounded-md border p-3 space-y-2 bg-white" data-testid="stock-adjuster">
            <div className="text-xs font-semibold text-neutral-700">Adjust stock</div>
            <div className="flex gap-3 text-xs">
                <label className="inline-flex items-center gap-1"><input type="radio" name="stkMode" checked={mode === 'set'} onChange={() => setMode('set')} data-testid="stk-mode-set" />Set to</label>
                <label className="inline-flex items-center gap-1"><input type="radio" name="stkMode" checked={mode === 'delta'} onChange={() => setMode('delta')} data-testid="stk-mode-delta" />Add / subtract</label>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label className="text-xs">{mode === 'set' ? `New stock (${unit})` : `Change \u00b1 (${unit})`}</Label>
                    <Input type="number" step="0.001" value={value} onChange={e => setValue(e.target.value)} placeholder={mode === 'set' ? '0' : 'e.g. 10 or -5'} data-testid="stk-value" />
                </div>
                <div>
                    <Label className="text-xs">Reason *</Label>
                    <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Restock, Damage" data-testid="stk-reason" />
                </div>
            </div>
            <Button size="sm" onClick={doSave} disabled={busy} data-testid="stk-save-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>{busy ? 'Saving\u2026' : 'Update stock'}</Button>
        </div>
    );
}


export default function ProductEdit() {
    const nav = useNavigate();
    const { id } = useParams();
    const isNew = id === 'new';
    const [form, setForm] = useState(emptyForm);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [taxGroups, setTaxGroups] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const [cr, br, tr, sr, ur] = await Promise.all([
                api.get('/categories'),
                api.get('/brands'),
                api.get('/tax-groups', { params: { active: true } }).catch(() => ({ data: [] })),
                api.get('/suppliers', { params: { active: true } }).catch(() => ({ data: { items: [] } })),
                api.get('/units', { params: { active: true } }).catch(() => ({ data: [] })),
            ]);
            setCategories(cr.data); setBrands(br.data);
            setTaxGroups(tr.data || []);
            setSuppliers((sr.data?.items) || sr.data || []);
            setUnits(ur.data || []);
            if (!isNew) {
                try {
                    const r = await api.get(`/products/${id}`);
                    setForm({
                        ...emptyForm, ...r.data,
                        images: r.data.images && r.data.images.length ? r.data.images : [''],
                        barcodes: r.data.barcodes || [],
                        supplier_ids: r.data.supplier_ids || [],
                        unit_prices: r.data.unit_prices || [],
                        retail_unit_code: r.data.retail_unit_code || 'pcs',
                        wholesale_unit_code: r.data.wholesale_unit_code || (r.data.retail_unit_code || 'pcs'),
                    });
                } catch { toast.error('Product not found'); nav('/dharmikdashboard/products'); }
            }
            setLoading(false);
        })();
    }, [id]);

    const selectedCat = categories.find(c => c.id === form.category_id);
    const specFields = selectedCat?.spec_fields || [];

    // Auto-inherit HSN when category changes (only if HSN currently empty)
    useEffect(() => {
        if (selectedCat?.hsn_code && !form.hsn_code) {
            setForm(f => ({ ...f, hsn_code: selectedCat.hsn_code }));
        }
    }, [selectedCat?.id]);

    const update = (path, val) => {
        setForm(f => {
            const c = JSON.parse(JSON.stringify(f));
            const keys = path.split('.'); let node = c;
            for (let i = 0; i < keys.length - 1; i++) node = node[keys[i]];
            node[keys[keys.length - 1]] = val;
            return c;
        });
    };

    const submit = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                images: form.images.filter(x => x.trim()),
                barcodes: form.barcodes.map(b => b.trim()).filter(Boolean),
            };
            if (isNew) await api.post('/products', payload);
            else {
                const { initial_inventory, inventory, ...rest } = payload;
                await api.patch(`/products/${id}`, rest);
            }
            toast.success(isNew ? 'Product created' : 'Product updated');
            nav('/dharmikdashboard/products');
        } catch (e) { toast.error(errMsg(e, 'Save failed')); }
        finally { setSaving(false); }
    };

    const unitOptions = useMemo(() => units.map(u => ({
        code: u.code, label: `${u.name}${u.symbol ? ` (${u.symbol})` : ''}${u.is_base ? ' — base' : ` — x${u.conversion_factor} ${u.base_unit_code}`}`,
    })), [units]);

    if (loading) return <AppShell pageTitle="Product"><LoadingBlock /></AppShell>;

    return (
        <AppShell pageTitle={isNew ? 'New Product' : 'Edit Product'} pageActions={
            <div className="flex gap-2">
                <Button variant="outline" onClick={() => nav('/dharmikdashboard/products')} data-testid="back-to-products-button"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
                <Button onClick={submit} disabled={saving} data-testid="save-product-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}><Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
            </div>
        }>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-8 space-y-4">
                    <Card className="p-4 space-y-3">
                        <div className="text-sm font-semibold">Basic Details</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><Label>SKU *</Label><Input value={form.sku} onChange={e => update('sku', e.target.value)} data-testid="product-sku-input" /></div>
                            <div><Label>Name *</Label><Input value={form.name} onChange={e => update('name', e.target.value)} data-testid="product-name-input" /></div>
                        </div>
                        <div><Label>Short Description</Label><Input value={form.short_description || ''} onChange={e => update('short_description', e.target.value)} data-testid="product-short-desc-input" /></div>
                        <div>
                            <Label>Full Description (A+ Content)</Label>
                            <RichTextEditor
                                value={form.description || ''}
                                onChange={(html) => update('description', html)}
                                placeholder="Write a rich product description with headings, bullet points, images, and links…"
                                testId="product-description-editor"
                            />
                            <div className="text-xs text-neutral-500 mt-1">Supports rich formatting: headings, lists, links, images, colours. Displayed on the product page.</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Category *</Label>
                                <Combobox
                                    options={categories.map(c => ({
                                        value: c.id,
                                        label: `${'\u2014'.repeat(c.level || 0)} ${c.name}`.trim(),
                                        hint: c.hsn_code ? `HSN ${c.hsn_code}` : (c.parent_id ? '' : 'Main'),
                                    }))}
                                    value={form.category_id}
                                    onChange={v => update('category_id', v)}
                                    placeholder="Search category\u2026"
                                    testId="product-category"
                                    clearable={false}
                                />
                            </div>
                            <div>
                                <Label>Brand</Label>
                                <Combobox
                                    options={brands.map(b => ({ value: b.id, label: b.name }))}
                                    value={form.brand_id || ''}
                                    onChange={v => update('brand_id', v)}
                                    placeholder="Search brand\u2026"
                                    testId="product-brand"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>HSN Code</Label>
                                <Input value={form.hsn_code || ''} onChange={e => update('hsn_code', e.target.value)} data-testid="product-hsn-input" placeholder="Auto-filled from category" />
                                <div className="text-xs text-neutral-500 mt-1">
                                    {selectedCat?.hsn_code ? <>Category HSN: <span className="font-mono">{selectedCat.hsn_code}</span> — override above if needed</> : 'Set HSN on the category to auto-inherit'}
                                </div>
                            </div>
                            <div>
                                <Label>Suppliers (multiple)</Label>
                                <MultiCombobox
                                    options={suppliers.map(s => ({
                                        value: s.id, label: s.name,
                                        hint: [s.city, s.state, s.gstin].filter(Boolean).join(' \u00b7 '),
                                    }))}
                                    value={form.supplier_ids || []}
                                    onChange={v => update('supplier_ids', v)}
                                    placeholder="Search suppliers\u2026"
                                    testId="product-supplier"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Barcodes (multiple)</Label>
                            <BarcodeInput value={form.barcodes} onChange={v => update('barcodes', v)} />
                        </div>
                    </Card>

                    <Card className="p-4 space-y-3">
                        <div className="text-sm font-semibold">Specifications (from category)</div>
                        {!selectedCat && <div className="text-xs text-neutral-500">Select a category to see specification fields.</div>}
                        {selectedCat && specFields.length === 0 && <div className="text-xs text-neutral-500">This category has no defined spec fields.</div>}
                        {specFields.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {specFields.map(f => (
                                    <div key={f.key}>
                                        <Label>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                                        {f.type === 'select' ? (
                                            <Select value={(form.specs || {})[f.key] || ''} onValueChange={v => update(`specs.${f.key}`, v)}>
                                                <SelectTrigger data-testid={`product-spec-${f.key}-select`}><SelectValue placeholder="Select" /></SelectTrigger>
                                                <SelectContent>{(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                                            </Select>
                                        ) : (
                                            <Input value={(form.specs || {})[f.key] || ''} onChange={e => update(`specs.${f.key}`, e.target.value)} data-testid={`product-spec-${f.key}-input`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card className="p-4 space-y-3">
                        <div className="text-sm font-semibold">Units &amp; Pricing</div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Retail Unit *</Label>
                                <Select value={form.retail_unit_code || 'pcs'} onValueChange={v => update('retail_unit_code', v)}>
                                    <SelectTrigger data-testid="product-retail-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                                    <SelectContent>{unitOptions.map(u => <SelectItem key={u.code} value={u.code}>{u.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Wholesale Unit *</Label>
                                <Select value={form.wholesale_unit_code || 'pcs'} onValueChange={v => update('wholesale_unit_code', v)}>
                                    <SelectTrigger data-testid="product-wholesale-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                                    <SelectContent>{unitOptions.map(u => <SelectItem key={u.code} value={u.code}>{u.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Stock Unit (inventory)</Label>
                                <Select value={form.stock_unit_code || 'none'} onValueChange={v => update('stock_unit_code', v === 'none' ? '' : v)}>
                                    <SelectTrigger data-testid="product-stock-unit"><SelectValue placeholder="Same as retail" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Same as retail unit</SelectItem>
                                        {unitOptions.filter(u => units.find(x => x.code === u.code)?.is_base).map(u => <SelectItem key={u.code} value={u.code}>{u.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <div className="text-xs text-neutral-500 mt-1">Inventory is tracked in a base unit</div>
                            </div>
                        </div>
                        <div>
                            <Label>Tax Group (GST rate)</Label>
                            <Select value={form.tax_group_id || 'none'} onValueChange={v => update('tax_group_id', v === 'none' ? '' : v)}>
                                <SelectTrigger data-testid="product-tax-group-select"><SelectValue placeholder="Select tax group" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No GST (0%)</SelectItem>
                                    {taxGroups.map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name} — {t.gst_rate}%
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-xs text-blue-900">
                            <Info className="h-4 w-4 mt-0.5 shrink-0" />
                            Pricing is stored as a matrix: (unit × customer type × tier). Retail customers see the retail row; wholesale customers see their tier row for the wholesale unit.
                        </div>
                        <UnitPriceMatrix
                            value={form.unit_prices}
                            onChange={v => update('unit_prices', v)}
                            retailUnit={form.retail_unit_code}
                            wholesaleUnit={form.wholesale_unit_code}
                        />
                    </Card>

                    <Card className="p-4 space-y-3">
                        <div className="text-sm font-semibold">Media (Images)</div>
                        <div className="space-y-3">
                            {form.images.map((img, idx) => (
                                <div key={idx} className="flex items-start gap-2 p-3 rounded-md border bg-neutral-50/50">
                                    <div className="flex-1 min-w-0">
                                        <ImageUpload
                                            value={img}
                                            folder="products"
                                            size="md"
                                            testId={`product-image-${idx}`}
                                            onChange={(url) => update(`images.${idx}`, url)}
                                            label={`Image ${idx + 1}${idx === 0 ? ' (main)' : ''}`}
                                        />
                                    </div>
                                    {form.images.length > 1 && (
                                        <Button type="button" variant="ghost" size="icon" onClick={() => update('images', form.images.filter((_, i) => i !== idx))} data-testid={`product-image-${idx}-remove-row`}>
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                            <Button type="button" variant="outline" size="sm" onClick={() => update('images', [...form.images, ''])} data-testid="product-image-add-slot">
                                <Plus className="h-3.5 w-3.5 mr-1" />Add another image
                            </Button>
                        </div>
                    </Card>

                    <Card className="p-4 space-y-3">
                        <div className="text-sm font-semibold">Logistics</div>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                            <div><Label>Weight (kg)</Label><Input type="number" step="0.1" value={form.logistics.weight_kg} onChange={e => update('logistics.weight_kg', parseFloat(e.target.value) || 0)} data-testid="logistics-weight-input" /></div>
                            <div><Label>L (cm)</Label><Input type="number" value={form.logistics.volumetric_l_cm || ''} onChange={e => update('logistics.volumetric_l_cm', parseFloat(e.target.value) || 0)} /></div>
                            <div><Label>W (cm)</Label><Input type="number" value={form.logistics.volumetric_w_cm || ''} onChange={e => update('logistics.volumetric_w_cm', parseFloat(e.target.value) || 0)} /></div>
                            <div><Label>H (cm)</Label><Input type="number" value={form.logistics.volumetric_h_cm || ''} onChange={e => update('logistics.volumetric_h_cm', parseFloat(e.target.value) || 0)} /></div>
                        </div>
                    </Card>
                </div>

                <div className="lg:col-span-4 space-y-4">
                    <Card className="p-4 space-y-3">
                        <div className="text-sm font-semibold">Status</div>
                        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => update('active', v)} data-testid="product-active-switch" /><Label>Active</Label></div>
                        <div className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={v => update('featured', v)} /><Label>Featured</Label></div>
                    </Card>
                    <Card className="p-4 space-y-3">
                        <div className="text-sm font-semibold">Inventory</div>
                        {isNew && (
                            <div>
                                <Label>Initial Stock (in {form.stock_unit_code || form.retail_unit_code || 'pcs'})</Label>
                                <Input type="number" step="0.001" value={form.initial_inventory} onChange={e => update('initial_inventory', parseFloat(e.target.value) || 0)} data-testid="initial-inventory-input" />
                            </div>
                        )}
                        {!isNew && (
                            <>
                                <div className="rounded-md bg-neutral-50 border p-3 text-xs">
                                    <div className="text-neutral-600">Current stock:
                                        <span className="ml-1 font-semibold tabular-nums text-neutral-900">{form.inventory?.available ?? 0}</span>
                                        {' '}{form.stock_unit_code || form.retail_unit_code || 'pcs'}
                                    </div>
                                    <div className="text-neutral-500 mt-0.5">Reserved: <span className="tabular-nums">{form.inventory?.reserved ?? 0}</span></div>
                                </div>
                                <StockAdjuster
                                    productId={id}
                                    unit={form.stock_unit_code || form.retail_unit_code || 'pcs'}
                                    currentQty={Number(form.inventory?.available ?? 0)}
                                    onAdjusted={(newQty) => update('inventory', { ...(form.inventory || {}), available: newQty })}
                                />
                            </>
                        )}
                        <div><Label>Low Stock Threshold</Label><Input type="number" step="0.001" value={form.low_stock_threshold} onChange={e => update('low_stock_threshold', parseFloat(e.target.value) || 0)} /></div>
                    </Card>
                    <Card className="p-4">
                        <div className="text-xs text-neutral-500">Tip: define spec fields and HSN on the category first, then products in that category will inherit them automatically.</div>
                    </Card>
                </div>
            </div>
        </AppShell>
    );
}
